import JSZip from 'jszip';
import { saveAs } from 'file-saver';

import { mediaIdOf } from 'src/utils/media';

import { getMediaContentBlob } from 'src/services/media/media-content-service-calls';

export const useFileHandler = () => {
  const downloadFile = (base64Data, fileType, fileName) => {
    try {
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);

      [...byteCharacters].forEach((char, i) => {
        byteNumbers[i] = char.charCodeAt(0);
      });

      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: fileType });

      const extension = fileType.split('/')[1];
      const fullFileName = `${fileName}.${extension}`;

      saveAs(blob, fullFileName);

      return {
        success: true,
        message: 'Archivo descargado exitosamente',
      };
    } catch (error) {
      console.error('Error downloading file:', error);
      return {
        success: false,
        message: 'Error al descargar el archivo',
        error,
      };
    }
  };

  const downloadFilesAsZip = async (files, zipName = 'archivos') => {
    try {
      const zip = new JSZip();

      const processFiles = files.map(async (file, index) => {
        if (file instanceof File || file instanceof Blob) {
          const fileName = file.name || `archivo_${index + 1}`;
          zip.file(fileName, file);
          return;
        }

        if (file.file instanceof File || file.file instanceof Blob) {
          const fileName = file.fileName || file.file.name || `archivo_${index + 1}`;
          zip.file(fileName, file.file);
          return;
        }

        const base64Data = file.base64Data || file.file;
        const { fileType } = file;

        if (!base64Data || !fileType) return;

        try {
          const byteCharacters = atob(base64Data);
          const byteNumbers = new Array(byteCharacters.length);

          [...byteCharacters].forEach((char, i) => {
            byteNumbers[i] = char.charCodeAt(0);
          });

          const byteArray = new Uint8Array(byteNumbers);
          const extension = fileType.split('/')[1];
          const fullFileName = file.fileName
            ? `${file.fileName}.${extension}`
            : `archivo_${index + 1}.${extension}`;

          zip.file(fullFileName, byteArray);
        } catch (err) {
          console.error(`Error processing file ${index}:`, err);
        }
      });

      await Promise.all(processFiles);

      if (Object.keys(zip.files).length === 0) {
        return {
          success: false,
          message: 'No hay archivos para comprimir',
        };
      }

      const content = await zip.generateAsync({ type: 'blob' });
      saveAs(content, `${zipName}.zip`);

      return {
        success: true,
        message: 'Archivos comprimidos descargados exitosamente',
      };
    } catch (error) {
      console.error('Error downloading zip file:', error);
      return {
        success: false,
        message: 'Error al descargar los archivos comprimidos',
        error,
      };
    }
  };

  // --------------------------------------------------------------------------
  // Descargas desde el endpoint de streaming del backend.
  //
  // El binario de un MediaBlob ya no viaja en base64 dentro del JSON, asi que
  // downloadFile/downloadFilesAsZip solo sirven para archivos que el navegador ya
  // tiene en memoria (capturas del escaner, adjuntos recien seleccionados). Para un
  // documento guardado hay que pedirlo al backend.
  // --------------------------------------------------------------------------

  /** Extension de cortesia para nombrar la descarga. */
  const extensionDe = (media, blob) => {
    const tipo = (media?.contentType ?? media?.fileType ?? blob?.type ?? '').split(';')[0].trim();
    const sufijo = tipo.split('/')[1];
    return sufijo ? `.${sufijo}` : '';
  };

  /**
   * Descarga un documento guardado (MediaBlob) por el endpoint de streaming.
   *
   * @param {object|string} media MediaBlob o mediaId.
   * @param {object} [opts]
   * @param {string} [opts.fileName] Nombre sin extension; se completa con el content-type.
   * @param {string} [opts.patientId] Ancla la descarga al paciente dueno del documento.
   */
  const downloadMedia = async (media, { fileName, patientId } = {}) => {
    if (!mediaIdOf(media)) {
      return { success: false, message: 'El documento no está disponible' };
    }

    try {
      const blob = await getMediaContentBlob(media, { patientId });
      const base = fileName || media?.fileName || 'documento';
      saveAs(blob, `${base}${extensionDe(media, blob)}`);
      return { success: true, message: 'Archivo descargado exitosamente' };
    } catch (error) {
      console.error('Error downloading media:', error);
      return { success: false, message: 'El documento no está disponible', error };
    }
  };

  /**
   * Descarga varios documentos guardados y los comprime en un ZIP.
   * Los documentos que el backend niegue (404) simplemente se omiten del ZIP.
   */
  const downloadMediasAsZip = async (medias, zipName = 'archivos', { patientId } = {}) => {
    try {
      const zip = new JSZip();

      await Promise.all(
        (medias ?? []).map(async (media, index) => {
          if (!mediaIdOf(media)) return;
          try {
            const blob = await getMediaContentBlob(media, { patientId });
            const base = media.fileName || `archivo_${index + 1}`;
            zip.file(`${base}${extensionDe(media, blob)}`, blob);
          } catch (err) {
            console.error(`Error processing media ${index}:`, err);
          }
        })
      );

      if (Object.keys(zip.files).length === 0) {
        return { success: false, message: 'No hay archivos para comprimir' };
      }

      const content = await zip.generateAsync({ type: 'blob' });
      saveAs(content, `${zipName}.zip`);

      return { success: true, message: 'Archivos comprimidos descargados exitosamente' };
    } catch (error) {
      console.error('Error downloading zip file:', error);
      return { success: false, message: 'Error al descargar los archivos comprimidos', error };
    }
  };

  return {
    downloadFile,
    downloadFilesAsZip,
    downloadMedia,
    downloadMediasAsZip,
  };
};
